import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProfileSection from './components/ProfileSection';
import PromotionSection from './components/PromotionSection';
import AboutSection from './components/AboutSection';
import CalendarSection from './components/CalendarSection';
import WorkSection from './components/WorkSection';
import ShopSection from './components/ShopSection';
import MobilePreviewModal from './components/MobilePreviewModal';
import { useArtistData } from '../../hooks/useArtistData';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminBroadcast } from '../../hooks/useAdminBroadcast';

const ArtistContentManagement = () => {
  const navigate = useNavigate();
  const { profileLoading } = useAuth();
  const { artistData, loading, error, updateArtistData } = useArtistData();
  const [activeSection, setActiveSection] = useState('profile');
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Extract artist brand colors for subtle admin theming
  const highlightColor = artistData?.profile?.highlightColor || '#6366F1';
  const secondaryColor = artistData?.profile?.secondaryColor || '#6366F1';

  const { 
    broadcastProfile,
    broadcastWorks,
    broadcastEvents,
    broadcastAbout,
    broadcastPromotions 
  } = useAdminBroadcast(artistData?.id);

  // Show loading state while profile or artist data is loading
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div
                  className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                  style={{ borderColor: `${highlightColor}40`, borderTopColor: 'transparent', borderLeftColor: highlightColor }}
                ></div>
                <p className="text-text-secondary">Loading your profile...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error loading data
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={() => window.location?.reload()}>Try Again</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artistData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="User" size={48} className="text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">No profile data available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'profile',
      name: 'Profile',
      icon: 'User',
      description: 'Basic information and contact details',
      required: true,
      completed: !!(artistData?.profile?.name && artistData?.profile?.email)
    },
    {
      id: 'promotion',
      name: 'Promotion',
      icon: 'Megaphone',
      description: 'App images and featured content',
      required: true,
      completed: !!(artistData?.promotion?.splashImage && artistData?.promotion?.logo)
    },
    {
      id: 'about',
      name: 'About',
      icon: 'FileText',
      description: 'Biography and booking information',
      required: true,
      completed: !!(artistData?.about?.aboutText && artistData?.about?.bookingContact?.name)
    },
    {
      id: 'calendar',
      name: 'Calendar',
      icon: 'Calendar',
      description: 'Events and performances',
      required: false,
      completed: artistData?.calendar?.length > 0
    },
    {
      id: 'work',
      name: 'Work',
      icon: 'Briefcase',
      description: 'Portfolio and projects',
      required: false,
      completed: artistData?.work?.length > 0
    },
    {
      id: 'shop',
      name: 'Shop',
      icon: 'ShoppingCart',
      description: 'Merchandise and products',
      required: false,
      completed: Object.values(artistData?.shop || {})?.some(Boolean)
    },
    {
      id: 'metrics',
      name: 'Metrics',
      icon: 'BarChart3',
      description: 'Mobile app analytics and insights',
      required: false,
      completed: false, // Always show as available but not required
      route: '/artist-metrics-dashboard'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: 'Bell',
      description: 'Push notifications to mobile users',
      required: false,
      completed: false, // Always show as available but not required
      route: '/artist-notifications-manager'
    }
  ];

  const handleSectionUpdate = async (section, data) => {
    const result = await updateArtistData(section, data);
    if (result?.error) {
      console.error('Update error:', result?.error);
      // You could add toast notification here
    }
  };

  // Handle section navigation
  const handleSectionClick = (section) => {
    if (section?.route) {
      navigate(section?.route);
    } else {
      setActiveSection(section?.id);
    }
  };

  const handleProfileSave = async (updatedProfile) => {
    try {
      setIsSaving(true);
      const result = await handleSectionUpdate('profile', updatedProfile);
      if (!result?.error) {
        await broadcastProfile(updatedProfile);
        setLastSaved(new Date());
      }
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setIsSaving(false);
    }
  };

  const handleWorkSave = async (workData, operation = 'UPDATE') => {
    try {
      setIsSaving(true);
      const result = await handleSectionUpdate('work', workData);
      if (!result?.error) {
        await broadcastWorks(workData, operation);
        setLastSaved(new Date());
      }
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving work:', error);
      setIsSaving(false);
    }
  };

  const handleEventSave = async (eventData, operation = 'UPDATE') => {
    try {
      setIsSaving(true);
      const result = await handleSectionUpdate('calendar', eventData);
      if (!result?.error) {
        await broadcastEvents(eventData, operation);
        setLastSaved(new Date());
      }
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving event:', error);
      setIsSaving(false);
    }
  };

  const handleAboutSave = async (aboutData) => {
    try {
      setIsSaving(true);
      const result = await handleSectionUpdate('about', aboutData);
      if (!result?.error) {
        await broadcastAbout(aboutData);
        setLastSaved(new Date());
      }
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving about:', error);
      setIsSaving(false);
    }
  };

  const handlePromotionsSave = async (promotionsData) => {
    try {
      setIsSaving(true);
      const result = await handleSectionUpdate('promotion', promotionsData);
      if (!result?.error) {
        await broadcastPromotions(promotionsData);
        setLastSaved(new Date());
      }
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving promotions:', error);
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-1">
                  <div
                    className="w-1 h-8 rounded-full"
                    style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor})` }}
                  />
                  <h1 className="text-3xl font-bold text-text-primary">Content Management</h1>
                </div>
                <p className="text-text-secondary mt-1 ml-4">
                  Manage your mobile app content across all sections
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowMobilePreview(true)}
                  iconName="Smartphone"
                  iconPosition="left"
                  iconSize={16}
                >
                  Preview App
                </Button>
                <Button
                  variant="default"
                  onClick={() => navigate('/mobile-app-preview')}
                  iconName="ExternalLink"
                  iconPosition="left"
                  iconSize={16}
                >
                  Full Preview
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Navigation */}
            <div className="lg:col-span-1">
              <div
                className="bg-surface rounded-lg border p-4 sticky top-24"
                style={{ borderColor: `${highlightColor}30` }}
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: highlightColor }}
                  />
                  <h3 className="text-lg font-semibold text-text-primary">Sections</h3>
                </div>
                
                <nav className="space-y-2">
                  {sections?.map((section) => {
                    const isActive = activeSection === section?.id && !section?.route;
                    return (
                      <button
                        key={section?.id}
                        onClick={() => handleSectionClick(section)}
                        className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'text-white' :'hover:bg-muted text-text-primary'
                        }`}
                        style={isActive ? {
                          background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor}CC)`,
                          boxShadow: `0 2px 8px ${highlightColor}40`
                        } : {}}
                      >
                        <div className="relative">
                          <Icon 
                            name={section?.icon} 
                            size={18} 
                            className={isActive ? 'text-white' : 'text-text-secondary'}
                          />
                          {section?.completed && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full flex items-center justify-center">
                              <Icon name="Check" size={8} color="white" />
                            </div>
                          )}
                          {section?.route && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                              <Icon name="ExternalLink" size={6} color="white" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{section?.name}</span>
                            {section?.required && (
                              <span
                                className="text-xs"
                                style={{ color: isActive ? 'rgba(255,255,255,0.8)' : secondaryColor }}
                              >*</span>
                            )}
                          </div>
                          <p className="text-xs opacity-70 truncate">{section?.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </nav>

                {/* Color accent strip at bottom of nav */}
                <div
                  className="mt-4 h-px rounded-full opacity-40"
                  style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor}, transparent)` }}
                />
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              <ProfileSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'profile'}
              />
              
              <PromotionSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'promotion'}
              />
              
              <AboutSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'about'}
              />
              
              <CalendarSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'calendar'}
              />
              
              <WorkSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'work'}
                artistType={artistData?.artistType}
              />
              
              <ShopSection
                artistData={artistData}
                onUpdate={handleSectionUpdate}
                isActive={activeSection === 'shop'}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Preview Modal */}
      <MobilePreviewModal
        isOpen={showMobilePreview}
        onClose={() => setShowMobilePreview(false)}
        artistData={artistData}
      />
    </div>
  );
};

export default ArtistContentManagement;